<template>
  <div class="tw:h-screen tw:flex tw:flex-col">
    <div
      ref="header"
      :class="{ 'tw:bg-white/70 tw:backdrop-blur-xs tw:shadow-xs': isScrolled }"
      aria-label="Główna nawigacja"
      class="tw:fixed tw:w-full tw:px-4 tw:md:px-6 tw:py-4 tw:flex tw:items-center tw:justify-between tw:z-20 tw:transition-all tw:duration-300"
      role="banner"
    >
      <a aria-label="Strona główna edrone" href="https://edrone.me/">
        <img
          alt="Logo edrone"
          class="tw:h-full"
          loading="eager"
          src="https://d3vhsxl1pwzf0p.cloudfront.net/logo-edrone-menu.svg"
        />
      </a>
      <div class="tw:flex tw:items-center tw:gap-4">
        <a class="tw:hidden tw:md:inline-block" href="/login">
          <EdroneButton type="text">{{ trans._register.login }}</EdroneButton>
        </a>
        <EdroneSelectCustom
          :allow-empty="false"
          :options="languageOptions"
          :selected-options="languageOptions.find((option) => option.value === language)"
          aria-label="Wybierz język"
          @select="handleLanguageChange"
        />
      </div>
    </div>
    <div
      class="tw:flex tw:flex-col tw:md:flex-row tw:flex-1 tw:gap-6 tw:md:gap-0 tw:bg-white tw:w-full tw:max-w-[160rem] tw:mx-auto"
      role="main"
    >
      <div
        aria-label="Formularz rejestracji"
        class="tw:md:h-full tw:md:flex-1 tw:flex tw:items-center tw:justify-center tw:bg-white tw:p-4 tw:md:p-0"
        role="region"
      >
        <RouterView name="main" />
      </div>
      <div
        aria-label="Informacje dodatkowe"
        class="tw:border-t tw:md:border-l tw:border-default-200 tw:md:flex-1 tw:bg-default-000 tw:md:bg-gradient-to-b tw:from-branding-100 tw:to-branding-500 tw:items-center tw:justify-center tw:flex tw:pb-24 tw:md:py-14 tw:px-4 tw:md:px-0"
        role="complementary"
      >
        <RouterView name="aside" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import EdroneButton from "edrone-components/EdroneButton.vue";
import EdroneSelectCustom from "edrone-components/EdroneSelectCustom.vue";
import { storeToRefs } from "pinia";
import { watch, ref, onMounted, onUnmounted } from "vue";
import { useRegisterStore } from "vue-app/pages/PageRegister/PageRegister.store";
import { useRouter } from "vue-router";
import { amplitudeSendEvent } from "vue-app/utils/amplitude";
import { saveUtmToCookies, removeUtmFromCookies } from "vue-app/pages/PageRegister/PageRegister.helpers";

const store = useRegisterStore();
const { language, selectedPlatform, email, shopUrl } = storeToRefs(store);

const header = ref<HTMLElement>();
const isScrolled = ref(false);
const engagementTimer = ref<number | null>(null);

const handleScroll = () => {
  isScrolled.value = window.scrollY > 10;
};

const handleBeforeUnload = () => {
  removeUtmFromCookies();
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
  window.addEventListener("beforeunload", handleBeforeUnload);
  saveUtmToCookies();

  setTimeout(() => {
    const query = router.currentRoute.value.query;

    if (!query.lang && !window.location.href.includes("chromatic.com")) {
      const browserLanguage = getBrowserLanguage();
      if (browserLanguage !== "pl") {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set("lang", browserLanguage);
        window.location.href = newUrl.toString();
        return;
      }
    }

    language.value = query.lang?.toString() || getBrowserLanguage();

    if (query.email) {
      email.value = query.email.toString();
    }
    if (query.shopUrl) {
      shopUrl.value = query.shopUrl.toString();
    }

    if (query.platform) {
      selectedPlatform.value = query.platform.toString();

      amplitudeSendEvent("register_platform_step_skipped", {
        platform: query.platform.toString(),
        source: "query_params",
      });

      router.push({
        name: "registerForm",
        query: { ...query },
      });
    }
  }, 1);
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("beforeunload", handleBeforeUnload);
  if (engagementTimer.value) {
    clearTimeout(engagementTimer.value);
  }
});

const languageOptions = [
  { value: "pl", name: "Polski" },
  { value: "en", name: "English" },
  { value: "pt", name: "Português" },
  { value: "es", name: "Español" },
];

const getBrowserLanguage = (): string => {
  // Skip browser language detection in Chromatic environment
  if (window.location.href.includes("chromatic.com")) {
    return "pl";
  }

  const browserLang = navigator.language.substring(0, 2);
  const supportedLanguages = languageOptions.map((option) => option.value);
  return supportedLanguages.includes(browserLang) ? browserLang : "pl";
};

const handleLanguageChange = (option: { value: string; name: string } | undefined) => {
  if (!option) return;

  const previousLanguage = language.value;
  language.value = option.value;

  amplitudeSendEvent("register_language_changed", {
    previous_language: previousLanguage,
    new_language: option.value,
    step: router.currentRoute.value.name === "registerForm" ? "form" : "platform_selection",
  });

  const newUrl = new URL(window.location.href);
  newUrl.searchParams.set("lang", option.value);

  window.location.href = newUrl.toString();
};

const router = useRouter();

watch(
  () => router.currentRoute.value.query.lang,
  (lang) => {
    language.value = lang?.toString() || getBrowserLanguage();
  }
);

watch(
  () => router.currentRoute.value.name,
  (newRouteName, oldRouteName) => {
    if (
      oldRouteName &&
      (oldRouteName.toString().includes("register") || oldRouteName.toString().includes("Register")) &&
      !newRouteName?.toString().includes("register") &&
      !newRouteName?.toString().includes("Register")
    ) {
      removeUtmFromCookies();
    }
  }
);
</script>

<style>
@media (max-width: 768px) {
  html {
    font-size: 16px !important;
  }
}

@media (min-width: 768px) {
  html {
    font-size: 12px !important;
  }
}

@media (min-width: 1024px) {
  html {
    font-size: 12px !important;
  }
}

@media (min-width: 1280px) {
  html {
    font-size: 12px !important;
  }
}

@media (min-width: 1536px) {
  html {
    font-size: 16px !important;
  }
}

@media (min-width: 1920px) {
  html {
    font-size: 17px !important;
  }
  .EdroneButton {
    height: 100% !important;
  }
}

@media (min-width: 2560px) {
  html {
    font-size: 24px !important;
  }
}

@media (min-width: 3840px) {
  html {
    font-size: 32px !important;
  }
}

@media (min-width: 5120px) {
  html {
    font-size: 36px !important;
  }
}
</style>
